<template>
    <header class="landing__nav">
        <nav class="navbar landing__nav__wrap">
            <router-link to="/" class="w--fit inline-block">
                <NewAppLogo position="left" page="index" width="80px" class="w--fit" />
            </router-link>
            
            <div v-if="windowWidth <= 768">
                <MobileNavIcon :is-transform="isTransform" @toggle="transformIcon" />
            </div>

            <div id="navContent" class="nav-content" :class="{ 'mobile': showMobileNav }">
                <ul class="nav-menu landing__nav__list">
                    <li 
                        v-for="item in navLinks" 
                        :key="item.id" 
                        class="nav-item pointer landing__nav__list__item"
                        :class="{ 'mobile': showMobileNav }"
                        @click="clickNavItem(item)"
                    >
                        <template v-if="!item.showDropdown">
                            <span class="landing__nav__link">
                                {{ item.name }}
                            </span>
                        </template>

                        <template v-else>
                            <div class="flex align-items-center">
                                <span 
                                    class="landing__nav__link mr--3" 
                                >
                                    {{ item.name }}
                                </span>
                                <IconSvg 
                                    v-if="item.showDropdown" 
                                    :name="currentNavItem === item.id ? 'chevron-up' : 'chevron-down'" 
                                    :fill="currentNavItem === item.id ? '#0891b2' : '#667085'"
                                />
                            </div>

                            <Transition v-if="navDropDownData" name="dropdown">
                                <div id="product-menu" class="mega-menu landing__nav__dropdown" :class="{ 'show': activeDropdown === item.id, 'mobile': showMobileNav }">
                                    <div class="mega-menu-content landing__nav__dropdown__content">
                                        <div 
                                            v-for="section in navDropDownData?.section"
                                            :key="section.title" 
                                            class="landing__nav__dropdown__section mega-menu-section"
                                        >
                                            <div class="section-title text--xs text--upper text--medium mega-menu-header">
                                                {{ section.title }}
                                            </div>

                                            <div
                                                v-for="link in section.links" 
                                                :key="link.id"
                                                class="flex align-items-center"
                                            >
                                                <template v-if="link.external">
                                                    <a 
                                                        :href="link.url" 
                                                        class="dropdown-link mega-menu-item"
                                                        target="_blank"
                                                    >
                                                        <div class="link-title">
                                                            {{ link.name }}
                                                        </div>
                                                        <div class="link-description">
                                                            {{ link.desc }}
                                                        </div>
                                                    </a>
                                                </template>
                                                    
                                                <template v-else>
                                                    <router-link 
                                                        class="dropdown-link mega-menu-item"
                                                        :to="link.url" 
                                                    >
                                                        <div class="link-title">
                                                            {{ link.name }}
                                                        </div>
                                                        <div class="link-description">
                                                            {{ link.desc }}
                                                        </div>
                                                    </router-link>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Transition>
                        </template>
                    </li>
                </ul>

                <div class="landing__nav__cta">
                    <template v-if="isAuthenticated">
                        <BaseButton 
                            label="Go to dashboard" 
                            size="md"
                            suffix-icon
                            class="text--sm"
                            icon-name="right-arrow-alt"
                            @submit="goToDashboard"
                        />
                    </template>
                    <template v-else>
                        <BaseButton 
                            label="Log in" 
                            class="text--sm" 
                            size="md"
                            variant="outline" 
                            medium
                            variant-style="primary"
                            @submit="goToLoginPage"
                        />
                        <BaseButton 
                            label="Sign up for free" 
                            size="md"
                            class="text--sm"
                            @submit="goToSignUpPage"
                        />
                    </template>
                </div>
            </div>
        </nav>
    </header>
</template>

<script>
import NewAppLogo from "../ui/logos/NewAppLogoSvg.vue";
import MobileNavIcon from "../ui/navigation/MobileNavIcon.vue";
import { IconSvg, BaseButton } from "../ui/index";

export default {
    name: "LandingHeader",

    components: { NewAppLogo, BaseButton, MobileNavIcon, IconSvg },

    props: {
        noInnerPadding: {
            type: Boolean,
            default: true,
        },
        pageType: {
            type: String,
            default: "home",
        },
    },

    data() {
        return {
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            showLinks: ["support", "home", "about_us", "terms", "privacy"],
            isTransform: false,
            showMobileNav: false,
            navLinks: [
                { name: "Product", id: "product", showDropdown: true, url: "" },
                { name: "Resources", id: "resources", showDropdown: true, url: ""  },
                { name: "Pricing", id: "pricing", showDropdown: false, url: "/pricing" },
                { name: "Contact", id: "support", showDropdown: false, url: "/support" },
            ],
            navLinksSub: {
                "product": {
                    section: [
                        {
                            title: "Manage",
                            links: [
                                { name: "Client management", id: "clients", desc: "Manage clients data", url: "/products/client-management", external: false },
                                { name: "Project & Tasks", id: "project", desc: "Organize your projects & tasks", url: "/products/projects-tasks", external: false },
                                { name: "Payments & Finance", id: "finance", desc: "Track and grow your finances", url: "/products/payments-and-finance", external: false },
                                // { name: "Proposals & Contracts", id: "proposals", desc: "Create and manage proposals", url: "/products/proposals", external: false },
                            ],
                        },
                        {
                            title: "Productivity",
                            links: [
                                { name: "Custom reminders", id: "reminders", desc: "Set automated reminders", url: "/products/reminders", external: false },
                                { name: "Workflows", id: "workflows", desc: "Enhance your workflows", url: "/products/workflows", external: false },
                                { name: "Integrations", id: "tasks", desc: "Connect to everyday tools", url: "/products/integrations", external: false },
                            ],
                        },
                        {
                            title: "Insights",
                            links: [
                                { name: "Reports", id: "reports", desc: "Get real time reports", url: "/products/reports", external: false },
                            ],
                        },
                    ],
                },
                "resources": {
                    section: [
                        {
                            title: "Company",
                            links: [
                                { name: "About us", id: "about", desc: "Why build Ziner?", url: "/about", external: false },
                                { name: "Substack blog", id: "blog", desc: "Read our blog", url: "https://www.uzoamakaanyanwu.com/posts/building-ziner/overview", external: true },
                            ],
                        },
                    ],
                },
            },
            hoverTimeout: null,
            activeDropdown: null,
            currentNavItem: "",
        };
    },

    computed: {
        isAuthenticated() {            
            return this.$store.getters["auth/isAuthenticated"];
        },

        navDropDownData() {
            if (!this.currentNavItem) return null;
            return this.navLinksSub[this.currentNavItem];
        },

        isDesktop() {
            return this.windowWidth > 992;
        },
    },

    mounted() {
        window.addEventListener("resize", this.onResize);
        document.addEventListener("click", this.handleOutsideClick);
                
        // Smooth page load animation
        document.body.style.opacity = "0";
        document.body.style.transform = "translateY(20px)";
                
        setTimeout(() => {
            document.body.style.transition = "all 0.6s ease";
            document.body.style.opacity = "1";
            document.body.style.transform = "translateY(0)";
        }, 100);
    },

    beforeUnmount() {
        document.removeEventListener("click", this.handleOutsideClick);
        clearTimeout(this.hoverTimeout);
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.onResize); 
    },

    methods: {
        transformIcon() {
            this.isTransform = !this.isTransform;
            this.showMobileNav = !this.showMobileNav;
        },

        goToDashboard() {
            return this.$router.push({ name: "reports", query: { type: "clients", range: "1_year" } });
        },

        goToLoginPage() {
            window.open(`${window.location.origin}/${"auth/login"}`, "_blank");
        },

        goToSignUpPage() {
            this.$store.dispatch("app/setSelectedPlan", "free");
            window.open(`${window.location.origin}/${"auth/signup"}`, "_blank");
        },

        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
        },

        handleOutsideClick(event) {
            const navbar = this.$refs.navbar;
            
            if (navbar && !navbar.contains(event.target)) {
                this.activeDropdown = null;
                this.currentNavItem = "";
            }
        },

        // clickMobileNavItem(item) {  
        //     console.log("clickMobileNavItem");
                      
        //     if (window.innerWidth > 968) return;

        //     if (this.currentNavItem) {
        //         if (this.currentNavItem === item.id) {
        //             this.currentNavItem = "";
        //             this.activeDropdown = null;
        //             return;
        //         }
                
        //         this.currentNavItem = item.id;
        //         this.activeDropdown = item.id;
        //         return;
        //     } else {
        //         this.currentNavItem = item.id;
        //         this.activeDropdown = item.id;
        //     }
        // },

        clickNavItem(item) {                                                
            if (!item.showDropdown) {
                if (!item.url) return;

                this.activeDropdown = null;
                this.currentNavItem = "";
                this.$router.push(item.url);
                return;
            }

            // if (this.windowWidth <= 968) {
                
            // };

            // Desktop: toggle on click as well for better UX       
            if (this.currentNavItem) {
                if (this.currentNavItem === item.id) {
                    this.currentNavItem = "";
                    this.activeDropdown = null;
                    return;
                }
                
                this.currentNavItem = item.id;
                this.activeDropdown = item.id;
                return;
            } else {
                this.currentNavItem = item.id;
                this.activeDropdown = item.id;
                return;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    @media (max-width: 768px) {
        .navbar {
            padding: 1rem;
        }

        .nav-links {
            gap: 1rem;
        }

        .dropdown {
            min-width: 90vw;
            left: -200px;
        }

        .dropdown-content {
            grid-template-columns: 1fr;
            gap: 1.5rem;
        }
    }

    header {
        position: sticky;
        top: 0;
        z-index: 1000;
    }

    .navbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: 81.1111%;
        margin: 0 auto;
        background-color: #f4f3ef !important;

        &.end {
            background-color: #1c1a16 !important;
        }
    }

    .nav-content {
        display: flex;
        align-items: center;
        gap: 3rem;
    }

    .nav-menu {
        display: flex;
        list-style: none;
        align-items: center;
    }

    .nav-item {
        position: relative;

        &.mobile {
            padding: 1rem;
        }
    }

    .nav-link {
        text-decoration: none;
        color: #4a5568;
        padding: 0px;
        display: flex;
        align-items: center;
        gap: 0.4rem;
        transition: color 0.2s;
        cursor: pointer;
        font-weight: 500;
    }

    /* Mega Menu Dropdown */
    .mega-menu {
        top: calc(100% + 1rem);
        left: 50%;
        transform: translateX(-50%);
        display: none;
        transition: opacity 0.3s;
    }

    .mega-menu.show {
        display: grid;
        // opacity: 1;
        animation: fadeIn 0.2s ease-in;
    }

    .mega-menu.mobile {
        transform: translateX(0%);
    }

    .mega-menu.show.mobile {
        background: transparent;
        animation: fadeInMobile 0.3s ease-in;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
    }

    @keyframes fadeInMobile {
        from { opacity: 0; transform: translateX(-0%) translateY(-10px); }
        to { opacity: 1; transform: translateX(-0%) translateY(0); }
    }

    .mega-menu-section {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .mega-menu-header {
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin-bottom: 0rem;
    }

    .mega-menu-item {
        text-decoration: none;
        border-radius: 6px;
        transition: all 0.2s;
        display: block;
    }

    /* Mobile Styles */
    @media (max-width: 968px) {
        .navbar {
            padding: 1rem 1.5rem;
            max-width: 100%;
        }

        .nav-content {
            position: fixed;
            left: -100%;
            top: 0;
            height: 100vh;
            width: 100%;
            max-width: 100%;
            background: white;
            flex-direction: column;
            align-items: flex-start;
            padding: 5rem 2rem 2rem;
            transition: left 0.3s;
            overflow-y: auto;
            gap: 0;
            box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
        }

        .nav-content.mobile {
            left: 0;
            top: 76px;
            background: #f1f0ed;
            padding-top: 2rem;
            padding-left: 1rem; 
        }

        .landing__nav__cta {
            margin-top: 30px;
        }

        .nav-menu {
            flex-direction: column;
            gap: 0;
            width: 100%;
        }

        .nav-item {
            width: 100%;

            &:hover {
                background-color: transparent;
            }

            &:not(:last-of-type) {
                border-bottom: 1px solid #e2e8f0;
            }
        }

        .nav-link {
            padding: 1.2rem 0;
            width: 100%;
        }

        .mega-menu.show {
            display: block;
            transform: none;
            margin-top: 1rem;
        }

        .mega-menu-item {
            padding: 0.6rem 0.75rem;
        }

        .mega-menu-header {
            margin-bottom: 0rem;
        }

        .mega-menu-section {
            border-left: 0.5px solid #00000017;
            border-right: none;
            padding-left: 0.75rem;
        }
    }
</style>